import type React from 'react';
import { useCallback, useEffect } from 'react';

import { computeWaveColor } from './WaveEffect.utils';

import styles from './WaveEffect.module.scss';

type WaveEffectProps = {
    /**
     * The CSS property of the target element to compute the color from.
     */
    colorProp?: 'background-color' | 'border-color' | 'color';
};

const TRANSITION_CLASSES = [styles.appear, styles.active];

const useWaveEffect = (
    targetRef: React.RefObject<HTMLElement>,
    waveRef: React.RefObject<HTMLElement>,
    options: WaveEffectProps = {}
) => {
    const { colorProp } = options;

    const handleClick = () => {
        // we push the computation to the next tick to ensure the target element
        // has the correct styles, mainly the width and height properties.
        requestAnimationFrame(() => {
            const wave = waveRef.current;
            const target = targetRef.current;
            wave?.classList.remove(...TRANSITION_CLASSES);
            if (!wave || !target) {
                return;
            }
            target.style.position = 'relative';
            const compStyles = getComputedStyle(target);
            wave.classList.add(...TRANSITION_CLASSES);
            wave.style.color = computeWaveColor(target, colorProp);
            wave.style.width = compStyles.getPropertyValue('width');
            wave.style.height = compStyles.getPropertyValue('height');
            const borderWidth = compStyles.getPropertyValue('border-width');
            wave.style.left = `-${borderWidth}`;
            wave.style.top = `-${borderWidth}`;
            wave.style.borderRadius = compStyles.getPropertyValue('border-radius');
        });
    };

    const handleTransitionEnd = useCallback(() => {
        waveRef.current?.classList.remove(...TRANSITION_CLASSES);
    }, []);

    useEffect(() => {
        waveRef.current?.addEventListener('transitionend', handleTransitionEnd);
        return () => {
            waveRef.current?.removeEventListener('transitionend', handleTransitionEnd);
        };
    }, []);

    useEffect(() => {
        targetRef.current?.addEventListener('click', handleClick);
        return () => {
            targetRef.current?.removeEventListener('click', handleClick);
        };
    }, []);

    return targetRef;
};

export { useWaveEffect };
